import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Box, Stack, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TopBar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import SectionLoader from "../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../component/EmptyState";
import { StatusChip } from "../../../../../../component/listing";
import { toneColors } from "../../../../../../component/statusTone";
import { getLeaveCalendar } from "../../../../../../services/leave";
import { getAllHolidays } from "../../../../../../services/holiday";
import { indexHolidays } from "../../../../../../utils/attendanceMonth";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont, radius } from "../../../../../../theme";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TONE = { Approved: "success", Pending: "warning", Rejected: "danger" };

const pad = (n) => String(n).padStart(2, "0");
const dayString = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
// how many names fit in a cell before the rest are summarised
const CELL_LIMIT = 3;

const firstNameOf = (n) => (n || "?").trim().split(/\s+/)[0];

// Who's off on a given day, as a chip. Colour carries the approval state, so a
// planner can tell committed absence from a request that might still be refused.
const PersonChip = ({ entry }) => {
  const { fg, bg } = toneColors(TONE[entry.status] || "muted");
  const half = entry.window && entry.window !== "full-day";
  return (
    <Tooltip
      arrow
      title={`${entry.employee} — ${entry.leaveType}${half ? ` (${entry.window.replace("-", " ")})` : ""} · ${entry.status}`}
    >
      <Box
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: fg,
          background: bg,
          border: `1px solid ${entry.status === "Pending" ? fg : "transparent"}`,
          borderStyle: entry.status === "Pending" ? "dashed" : "solid",
          borderRadius: "6px",
          px: 0.5,
          py: "4px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
      >
        {firstNameOf(entry.employee)}
        {half ? " ½" : ""}
      </Box>
    </Tooltip>
  );
};

/**
 * Team leave as a month grid.
 *
 * A per-person register reads fine as a sequence, but planning is a spatial
 * question — "which week is thin, when is everyone in?" — and that is answered
 * as much by the empty cells as the full ones. The previous list rendered only
 * days that had leave, so the days you could actually schedule into were the
 * ones it left out.
 */
const TeamLeaveCalendar = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [rows, setRows] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const requestId = useRef(0);
  const { year, month } = cursor;

  const fetchData = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setError(false);
    const from = dayString(year, month, 1);
    const to = dayString(year, month, new Date(Date.UTC(year, month + 1, 0)).getUTCDate());

    const [res, holRes] = await Promise.all([getLeaveCalendar({ from, to }), getAllHolidays()]);
    if (id !== requestId.current) return;

    if (res?.success === false) {
      setError(true);
      notify.error(res?.message || "Failed to load leave calendar");
    }
    setRows(Array.isArray(res?.data) ? res.data : []);
    setHolidays(Array.isArray(holRes?.data) ? holRes.data : []);
    setLoading(false);
  }, [year, month, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setSelectedDay(null); }, [year, month]);

  const holidayByDay = useMemo(() => indexHolidays(holidays), [holidays]);

  // expand each request's days into a per-day map, ignoring days outside the month
  const byDay = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      (r.days || []).forEach((d) => {
        const dt = new Date(d.date);
        if (dt.getUTCMonth() !== month || dt.getUTCFullYear() !== year) return;
        const key = dayString(year, month, dt.getUTCDate());
        (map[key] = map[key] || []).push({
          employee: r.employee,
          leaveType: r.leaveType,
          status: r.status,
          window: d.window,
        });
      });
    });
    return map;
  }, [rows, month, year]);

  // A month is 4-6 week rows. Leading and trailing blanks keep every column on
  // the same weekday, which is the whole point of the grid.
  const weeks = useMemo(() => {
    const total = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const lead = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const cells = [
      ...Array.from({ length: lead }, () => null),
      ...Array.from({ length: total }, (_, i) => i + 1),
    ];
    while (cells.length % 7 !== 0) cells.push(null);
    return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
  }, [year, month]);

  const todayString = dayString(today.getFullYear(), today.getMonth(), today.getDate());
  const totalOff = Object.values(byDay).reduce((s, list) => s + list.length, 0);
  const selected = selectedDay ? byDay[selectedDay] || [] : null;

  const step = (delta) =>
    setCursor((c) => {
      const next = new Date(Date.UTC(c.year, c.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading={"Team Leave Calendar"}
        breadcrumbs={[{ label: "Leave", href: "/app/leave-dashboard" }, { label: "Calendar" }]}
      />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: "1.25rem", flexWrap: "wrap", gap: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton aria-label="Previous" size="small" onClick={() => step(-1)} sx={{ border: `1px solid ${tokens.border}` }}>
              <ChevronLeftIcon sx={{ fontSize: "1.125rem" }} />
            </IconButton>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, minWidth: "10rem", textAlign: "center" }}>
              {MONTHS[month]} {year}
            </Box>
            <IconButton aria-label="Next" size="small" onClick={() => step(1)} sx={{ border: `1px solid ${tokens.border}` }}>
              <ChevronRightIcon sx={{ fontSize: "1.125rem" }} />
            </IconButton>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap" }}>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
              {totalOff === 0
                ? "Nobody off this month"
                : `${totalOff} day${totalOff === 1 ? "" : "s"} of leave booked`}
            </Box>
            {/* the return leg of the dashboard's Strip/Calendar toggle */}
            <Stack
              direction="row"
              sx={{ border: `1px solid ${tokens.border}`, borderRadius: "999px", p: "4px", background: tokens.card }}
            >
              <Box
                component="button"
                type="button"
                aria-pressed={false}
                onClick={() => navigate("/app/leave-dashboard")}
                sx={{
                  font: "inherit",
                  fontSize: "0.8125rem",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: "999px",
                  px: 1.5,
                  py: 0.5,
                  color: tokens.textMuted,
                  background: "transparent",
                  "&:hover": { color: tokens.text },
                  "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                }}
              >
                Strip
              </Box>
              <Box
                component="button"
                type="button"
                aria-pressed
                sx={{
                  font: "inherit",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  border: "none",
                  cursor: "default",
                  borderRadius: "999px",
                  px: 1.5,
                  py: 0.5,
                  color: tokens.onPrimary,
                  background: tokens.primary,
                }}
              >
                Calendar
              </Box>
            </Stack>
          </Stack>
        </Stack>

        {loading ? (
          <SectionLoader />
        ) : error ? (
          <EmptyState variant="error" title="Couldn't load calendar" onRetry={fetchData} />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: "44rem" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", mb: 0.5 }}>
                  {WEEKDAYS.map((w) => (
                    <Box
                      key={w}
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: tokens.textMuted,
                        textAlign: "center",
                        pb: 0.5,
                      }}
                    >
                      {w}
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "4px",
                    background: tokens.border,
                    border: `1px solid ${tokens.border}`,
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  {weeks.flat().map((d, i) => {
                    if (d === null) {
                      return <Box key={`blank-${i}`} sx={{ background: tokens.surface, minHeight: "6rem" }} />;
                    }
                    const key = dayString(year, month, d);
                    const people = byDay[key] || [];
                    const holidayName = holidayByDay.get(key);
                    const isToday = key === todayString;
                    const isSelected = key === selectedDay;
                    const shown = people.slice(0, CELL_LIMIT);
                    const extra = people.length - shown.length;

                    return (
                      <Box
                        key={key}
                        role={people.length ? "button" : undefined}
                        tabIndex={people.length ? 0 : -1}
                        aria-pressed={people.length ? isSelected : undefined}
                        onClick={() => people.length && setSelectedDay(isSelected ? null : key)}
                        onKeyDown={(e) => {
                          if (!people.length || (e.key !== "Enter" && e.key !== " ")) return;
                          e.preventDefault();
                          setSelectedDay(isSelected ? null : key);
                        }}
                        sx={{
                          background: isSelected ? `${tokens.accentTint}` : tokens.card,
                          minHeight: "6rem",
                          p: 1,
                          cursor: people.length ? "pointer" : "default",
                          boxShadow: isSelected ? `inset 0 0 0 2px ${tokens.accent}` : "none",
                          transition: "background-color 120ms cubic-bezier(0.2, 0, 0.2, 1)",
                          "&:hover": people.length ? { background: tokens.surfaceSubtle } : undefined,
                          "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "-2px" },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 0.5, gap: 0.5 }}
                        >
                          <Box
                            sx={{
                              fontSize: "0.8125rem",
                              fontWeight: isToday ? 700 : 500,
                              fontVariantNumeric: "tabular-nums",
                              color: isToday ? tokens.onPrimary : tokens.textMuted,
                              background: isToday ? tokens.primary : "transparent",
                              borderRadius: "999px",
                              minWidth: "1.25rem",
                              height: "1.25rem",
                              lineHeight: 1.5,
                              textAlign: "center",
                            }}
                          >
                            {d}
                          </Box>
                          {holidayName && (
                            <Tooltip arrow title={holidayName}>
                              <Box
                                sx={{
                                  fontSize: "0.8125rem",
                                  fontWeight: 600,
                                  color: tokens.textMuted,
                                  background: tokens.surfaceSubtle,
                                  borderRadius: "6px",
                                  px: 0.5,
                                  lineHeight: 1.5,
                                }}
                              >
                                HOLIDAY
                              </Box>
                            </Tooltip>
                          )}
                        </Stack>

                        <Stack spacing={0.375} sx={{ minWidth: 0 }}>
                          {shown.map((e, idx) => (
                            <PersonChip key={idx} entry={e} />
                          ))}
                          {extra > 0 && (
                            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, pl: 0.5 }}>
                              +{extra} more
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap", mt: 1.5 }}>
              {["Approved", "Pending"].map((s) => (
                <Stack key={s} direction="row" alignItems="center" spacing={0.625}>
                  <Box
                    sx={{
                      width: "0.75rem",
                      height: "0.75rem",
                      borderRadius: "6px",
                      background: toneColors(TONE[s]).bg,
                      border: `1px ${s === "Pending" ? "dashed" : "solid"} ${toneColors(TONE[s]).fg}`,
                    }}
                  />
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{s}</Box>
                </Stack>
              ))}
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>½ = half day</Box>
            </Stack>

            {selected && (
              <Box
                sx={{
                  mt: 2,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: `${radius.card}px`,
                  background: tokens.card,
                  p: "1rem 1.25rem",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  sx={{ mb: 1, gap: 1, flexWrap: "wrap" }}
                >
                  <Box sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
                    {selectedDay} · {WEEKDAYS[new Date(`${selectedDay}T00:00:00Z`).getUTCDay()]}
                  </Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                    {selected.length} away
                  </Box>
                </Stack>
                <Stack spacing={0.75}>
                  {selected.map((e, i) => (
                    <Stack key={i} direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                      <Box sx={{ fontSize: "0.875rem", fontWeight: 600, minWidth: "9rem" }}>
                        {e.employee}
                      </Box>
                      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                        {e.leaveType}
                        {e.window && e.window !== "full-day" ? ` · ${e.window.replace("-", " ")}` : ""}
                      </Box>
                      <StatusChip label={e.status} tone={TONE[e.status]} />
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </MainBox>
    </Box>
  );
};

export default TeamLeaveCalendar;
